import * as React from "react";
import { cn } from "@/lib/utils";
import { useThemeMode } from "@/hooks/useThemeMode";
import { 
  useEnhancedTheme, 
  getGlassMorphismClasses, 
  getNeumorphismClasses
} from "@/hooks/useEnhancedTheme";
import { motion, MotionProps } from "framer-motion";

interface EnhancedCardProps {
  children: React.ReactNode;
  className?: string;
  hoverEffect?: boolean;
  innerGlow?: boolean;
  outerGlow?: boolean;
  gradient?: boolean;
  motionProps?: MotionProps;
}

export const EnhancedCard = React.forwardRef<HTMLDivElement, EnhancedCardProps>(
  ({ 
    children, 
    className, 
    hoverEffect = true,
    innerGlow = false,
    outerGlow = false,
    gradient = false,
    motionProps,
    ...props 
  }, ref) => {
    const { isDark } = useThemeMode();
    const { glassMorphism, neumorphism } = useEnhancedTheme();
    const [isHovering, setIsHovering] = React.useState(false);

    return (
      <motion.div
        ref={ref}
        className={cn(
          "rounded-xl overflow-hidden border-2",
          getGlassMorphismClasses(glassMorphism, isDark),
          getNeumorphismClasses(neumorphism, isDark),
          hoverEffect ? "transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:border-primary/60" : "",
          className
        )}
        onHoverStart={() => setIsHovering(true)}
        onHoverEnd={() => setIsHovering(false)}
        whileHover={hoverEffect ? { scale: 1.02 } : undefined}
        whileTap={hoverEffect ? { scale: 0.98 } : undefined}
        {...motionProps}
        {...props}
      >
        {/* Optional inner glow - sharp version */}
        {innerGlow && (
          <div className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none">
            <div 
              className={cn(
                "absolute inset-0 opacity-0 transition-opacity duration-300",
                isDark 
                  ? "bg-gradient-to-br from-primary/40 via-transparent to-transparent border-2 border-primary/30" 
                  : "bg-gradient-to-br from-primary/30 via-transparent to-transparent border-2 border-primary/20"
              )}
              style={{ 
                opacity: isHovering ? 0.8 : 0,
              }}
            />
          </div>
        )}
        
        {children}
        
        {/* Sharp shine animation on hover */}
        {hoverEffect && (
          <motion.div 
            className="absolute inset-0 pointer-events-none overflow-hidden rounded-xl opacity-0"
            animate={{ opacity: isHovering ? 0.6 : 0 }}
          >
            <motion.div
              className="w-20 h-full bg-gradient-to-r from-transparent via-white to-transparent"
              animate={
                isHovering 
                  ? { x: ["calc(-100%)", "calc(400%)"] }
                  : { x: "calc(-100%)" }
              }
              transition={{
                duration: 0.8,
                ease: "easeInOut",
              }}
            />
          </motion.div>
        )}
      </motion.div>
    );
  }
);

EnhancedCard.displayName = "EnhancedCard";

interface MotionHeaderProps {
  className?: string;
  children: React.ReactNode;
}

export const EnhancedCardHeader = React.forwardRef<
  HTMLDivElement,
  MotionHeaderProps
>(({ className, ...props }, ref) => (
  <motion.div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    initial={{ opacity: 0, y: -20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    {...props}
  />
));

EnhancedCardHeader.displayName = "EnhancedCardHeader";

interface MotionTitleProps {
  className?: string;
  children: React.ReactNode;
}

export const EnhancedCardTitle = React.forwardRef<
  HTMLHeadingElement,
  MotionTitleProps
>(({ className, ...props }, ref) => (
  <motion.h3
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.3, delay: 0.1 }}
    {...props}
  />
));

EnhancedCardTitle.displayName = "EnhancedCardTitle";

interface MotionDescriptionProps {
  className?: string;
  children: React.ReactNode;
}

export const EnhancedCardDescription = React.forwardRef<
  HTMLParagraphElement,
  MotionDescriptionProps
>(({ className, ...props }, ref) => (
  <motion.p
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.3, delay: 0.2 }}
    {...props}
  />
));

EnhancedCardDescription.displayName = "EnhancedCardDescription";

interface MotionContentProps {
  className?: string;
  children: React.ReactNode;
}

export const EnhancedCardContent = React.forwardRef<
  HTMLDivElement,
  MotionContentProps
>(({ className, ...props }, ref) => (
  <motion.div 
    ref={ref} 
    className={cn("p-6 pt-0", className)}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay: 0.15 }}
    {...props} 
  />
));

EnhancedCardContent.displayName = "EnhancedCardContent";

interface MotionFooterProps {
  className?: string;
  children: React.ReactNode;
}

export const EnhancedCardFooter = React.forwardRef<
  HTMLDivElement,
  MotionFooterProps
>(({ className, ...props }, ref) => (
  <motion.div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.3, delay: 0.3 }}
    {...props}
  />
));

EnhancedCardFooter.displayName = "EnhancedCardFooter";
